// External sources (Plex + Jellyfin) — Pinia store.
//
// Mirrors the /api/sources/* endpoints. State is intentionally NOT persisted —
// the device cookie is the source of truth on the server, and a stale local
// copy of "connected accounts" the moment after an admin revokes the invite
// would be misleading. All actions are no-ops when the caller lacks the
// `connect-external-sources` permission; we return safe defaults ([] / null)
// rather than throw so call sites can stay simple.
//
// `getMatch(tmdbId, type)` is the hot-path action: it backs the source-match
// pills rendered on every MediaCard in /discover. We dedupe concurrent calls
// keyed by `tmdb_id + type` and cache results for 30 s in-memory so a grid of
// 20 cards rendering the same item never fans out 20 requests.

import { defineStore } from "pinia";
import { ApiError } from "@/services/apiClient";
import * as sourcesApi from "@/services/sources";
import { useAuthStore } from "@/stores/auth";

// Module-local dedupe + result cache for `getMatch`. Kept off reactive state
// so the Map references stay stable across the store's lifetime — Pinia's
// reactive proxy would otherwise wrap Map values and break equality checks
// inside the dedupe logic. Both maps are cleared on logout via the store's
// `reset()` action.
//
// `matchCache` is soft-capped at MATCH_CACHE_MAX_ENTRIES so a long Discover
// session that scrolls through thousands of titles doesn't grow the cache
// unbounded. We rely on Map's insertion-order iteration: when we cross the
// cap on a write, we evict the oldest entries (≈10% of cap) in one pass so
// we don't pay the cost on every subsequent write. Eviction picks the
// FIRST inserted keys, which approximates LRU as long as writes refresh
// entries (and they do — every `getMatch` resolve writes via `set`, which
// only updates value in place for an existing key; we re-insert below to
// make it a true MRU on hit).
const matchInflight = new Map(); // key -> Promise<Array>
const matchCache = new Map(); // key -> { value, expiresAt }
const MATCH_CACHE_TTL_MS = 30_000;
const MATCH_CACHE_MAX_ENTRIES = 256;
const MATCH_CACHE_EVICT_BATCH = 32;
let matchRateLimitedUntil = 0;

function setMatchCacheEntry(key, entry) {
  // If already present, delete first so the re-insert pushes to the tail
  // (MRU position) — Map iteration is insertion-order, so eviction picks
  // the oldest first.
  if (matchCache.has(key)) {
    matchCache.delete(key);
  }
  matchCache.set(key, entry);
  if (matchCache.size > MATCH_CACHE_MAX_ENTRIES) {
    // Evict a batch in one pass to amortize cost.
    let toEvict = MATCH_CACHE_EVICT_BATCH;
    for (const k of matchCache.keys()) {
      if (toEvict-- <= 0) break;
      matchCache.delete(k);
    }
  }
}

// True after a 403 has been observed in the current session. Prevents a chain
// of 4 concurrent sources actions from emitting 4 identical "permission
// revoked" toasts. Reset by `reset()` so a fresh login starts clean.
let permissionToastFired = false;

function matchKey(tmdbId, type) {
  return `${type}:${tmdbId}`;
}

function normalizeTmdbId(value) {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^[1-9]\d{0,9}$/.test(trimmed)) {
      return Number.parseInt(trimmed, 10);
    }
  }
  return null;
}

// Surface a one-shot toast if `useToast` is wired up; otherwise console.warn.
// Imported lazily so server-side or test environments without the composable
// fall back gracefully. The first call after `reset()` shows a toast; every
// subsequent call until the next `reset()` is silent (still console.warn'd).
async function surfacePermissionToast(message) {
  if (permissionToastFired) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn("[sources]", message, "(toast suppressed — already shown)");
    }
    return;
  }
  permissionToastFired = true;
  try {
    const mod = await import("@/composables/useToast");
    if (mod && typeof mod.useToast === "function") {
      mod.useToast().error(message);
      return;
    }
  } catch {
    // useToast unavailable — fall through to console.
  }
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    console.warn("[sources]", message);
  }
}

// localStorage key for a Plex PIN flow currently in-flight. Persist
// `{ pinId, authUrl, deadline }` here when the flow starts so a same-tab
// redirect to plex.tv can resume polling on return without re-asking
// the server for a fresh PIN. Cleared on success / expiry / cancel.
const PENDING_PLEX_STORAGE_KEY = "sources:plex:pending";

function _readPendingPlexConnect() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(PENDING_PLEX_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.pinId || !parsed.authUrl) return null;
    if (typeof parsed.deadline !== "number" || parsed.deadline < Date.now()) {
      localStorage.removeItem(PENDING_PLEX_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function _writePendingPlexConnect(payload) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(PENDING_PLEX_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / disabled storage — silently fall back to in-memory only */
  }
}

function _clearPendingPlexConnect() {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(PENDING_PLEX_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export const useSourcesStore = defineStore("sources", {
  state: () => ({
    plexAccounts: [],
    jellyfinServers: [],
    loading: false,
    error: null,
    // While a Plex PIN flow is in progress, this is set to:
    //   { pinId, authUrl, popupBlocked, expiresAt }
    // Components can render fallback affordances (copyable URL, "Open
    // in new tab", "Open in this tab") when popupBlocked === true so a
    // popup-blocker doesn't kill the connect path. Cleared on
    // connect / expire / cancel.
    pendingPlexConnect: null,
  }),

  getters: {
    // Proxies the auth store's `canConnectExternalSources` flag. Components
    // can `const sources = useSourcesStore()` + check `sources.canConnect`
    // without separately importing the auth store.
    canConnect() {
      try {
        const auth = useAuthStore();
        return auth.canConnectExternalSources === true;
      } catch {
        // Pinia not ready (shouldn't happen during a normal render).
        return false;
      }
    },
    // Feature 28: the lower-tier gate. True when the device can BROWSE
    // and PLAY external sources (their own OR ones shared with them), but
    // may not have permission to add/remove/refresh sources. Sharees with
    // only `use-external-sources` flip this true and `canConnect` false.
    // `canConnect` implies `canUse` (admins also flip both true).
    canUse() {
      try {
        const auth = useAuthStore();
        return auth.canUseExternalSources === true;
      } catch {
        return false;
      }
    },
  },

  actions: {
    // -- Internal helpers ------------------------------------------------

    /**
     * Map a thrown error from any `sourcesApi.*` call into store state.
     * Returns `true` if the call should short-circuit (caller bails with
     * a safe default). 403s clear the store + emit one toast. Other
     * errors set `this.error` so the UI can render an inline message.
     */
    _handleError(err, { silent } = {}) {
      if (err instanceof ApiError && err.status === 403) {
        // The permission was revoked under us OR the device lost it for
        // some other reason. Wipe local state so stale cards stop rendering
        // and surface a single toast — never retry.
        this.plexAccounts = [];
        this.jellyfinServers = [];
        matchInflight.clear();
        matchCache.clear();
        this.error = err;
        if (!silent) {
          surfacePermissionToast(
            "External-sources permission has been revoked",
          );
        }
        return true;
      }
      this.error = err;
      return false;
    },

    // -- Plex ------------------------------------------------------------

    async loadPlex() {
      // Feature 28: read paths gate on the broader `canUse` so sharees
      // with use-only permission can see their synthetic "Shared by ..."
      // accounts. Write actions (connect, refresh, disconnect) still
      // gate on `canConnect` further down.
      if (!this.canUse) {
        this.plexAccounts = [];
        return [];
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await sourcesApi.listAccounts();
        this.plexAccounts = Array.isArray(res)
          ? res
          : Array.isArray(res?.accounts)
            ? res.accounts
            : [];
        return this.plexAccounts;
      } catch (err) {
        this._handleError(err);
        return [];
      } finally {
        this.loading = false;
      }
    },

    async connectPlex() {
      if (!this.canConnect) return null;
      this.error = null;
      try {
        const res = await sourcesApi.connectPlexStart();
        // Server returns { pinId, authUrl, expiresAt }
        return res ?? null;
      } catch (err) {
        this._handleError(err);
        return null;
      }
    },

    async pollPlexConnect(pinId) {
      if (!this.canConnect) return null;
      try {
        return await sourcesApi.pollPlexConnect(pinId);
      } catch (err) {
        this._handleError(err);
        return null;
      }
    },

    /**
     * End-to-end Plex PIN-OAuth flow.
     *
     * Opens the plex.tv auth URL in a popup, polls the backend once per
     * second, and resolves when the server reports `status: 'connected'`
     * (success), `status: 'expired'` (PIN TTL hit), the popup closes
     * before completion, or the overall `pinTtlMs` budget is exhausted.
     *
     * The popup also calls `window.opener.postMessage({ type: 'plex-connect-return' })`
     * on `/sources/connect/plex/return`, which short-circuits the next
     * poll so the UI updates a little quicker than the 1 s tick.
     *
     * NEVER throws — returns one of:
     *   { ok: true, accountId, account? }      // happy path
     *   { ok: false, reason: 'expired' }       // server reports expired
     *   { ok: false, reason: 'timeout' }       // pinTtlMs elapsed
     *   { ok: false, reason: 'popup_closed' }  // user closed popup early
     *   { ok: false, reason: 'popup_blocked' } // window.open returned null
     *   { ok: false, reason: 'no_permission' } // canConnect=false
     *   { ok: false, reason: 'start_failed', error }
     *   { ok: false, reason: 'error', error }  // unexpected
     *
     * @param {{ pinTtlMs?: number, pollIntervalMs?: number, popupClosedGraceMs?: number }} [opts]
     */
    async connectPlexFlow(opts = {}) {
      if (!this.canConnect) {
        return { ok: false, reason: "no_permission" };
      }
      const pinTtlMs = Number.isFinite(opts.pinTtlMs)
        ? opts.pinTtlMs
        : 30 * 60 * 1000; // 30 min — matches the Plex PIN TTL
      const pollIntervalMs = Number.isFinite(opts.pollIntervalMs)
        ? opts.pollIntervalMs
        : 1000;
      // Brief grace after a 'connected' postMessage so window.opener has
      // time to read the message before we tear the popup reference down.
      const popupClosedGraceMs = Number.isFinite(opts.popupClosedGraceMs)
        ? opts.popupClosedGraceMs
        : 1500;

      this.error = null;

      // 1) Ask the backend for { pinId, authUrl }.
      let start;
      try {
        start = await sourcesApi.connectPlexStart();
      } catch (err) {
        this._handleError(err);
        return { ok: false, reason: "start_failed", error: err };
      }
      const pinId = start?.pinId;
      const authUrl = start?.authUrl;
      if (!pinId || !authUrl) {
        return { ok: false, reason: "start_failed", error: start };
      }

      // 2) Open the popup. Must be a synchronous call following a user
      // gesture to avoid popup blockers — callers should invoke
      // connectPlexFlow() directly from a click handler.
      let popup = null;
      try {
        if (
          typeof window !== "undefined" &&
          typeof window.open === "function"
        ) {
          const features =
            "width=520,height=720,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes";
          popup = window.open(authUrl, "plex-connect", features);
          // Best-effort focus — popup blockers ignore failed calls silently.
          if (popup && typeof popup.focus === "function") {
            try {
              popup.focus();
            } catch {
              /* ignore */
            }
          }
        }
      } catch {
        popup = null;
      }
      // Surface the in-flight state to the UI so components can render
      // the popup-blocked fallback (copyable URL, "Open in new tab" /
      // "Open in this tab" affordances) without us having to early-
      // return — polling continues regardless of whether the popup
      // opened. A user who authorizes in a tab they opened manually
      // still hits the same /sources/connect/plex/return view, which
      // postMessages this window and wakes the poll loop. We also
      // persist the pin to localStorage so a same-tab redirect to
      // plex.tv can resume polling on return without re-asking the
      // server for a fresh PIN.
      const deadline = Date.now() + pinTtlMs;
      this.pendingPlexConnect = {
        pinId,
        authUrl,
        popupBlocked: !popup,
        expiresAt: deadline,
      };
      _writePendingPlexConnect({
        pinId,
        authUrl,
        deadline,
      });

      // 3) Wire up postMessage from the return view as a polling hint.
      // We don't trust the message to decide success — only the server
      // poll can do that. The hint just wakes up an in-flight sleep so
      // the next poll fires immediately instead of waiting out the
      // remaining tick.
      let wakeSleep = null;
      const onMessage = (event) => {
        try {
          if (typeof window === "undefined") return;
          if (event?.origin !== window.location.origin) return;
          const data = event?.data;
          if (
            data &&
            typeof data === "object" &&
            data.type === "plex-connect-return"
          ) {
            // Wake any pending sleep; ignore if no sleep is active.
            if (typeof wakeSleep === "function") {
              const w = wakeSleep;
              wakeSleep = null;
              w();
            }
          }
        } catch {
          // Ignore — fall back to the timer-driven poll.
        }
      };
      if (
        typeof window !== "undefined" &&
        typeof window.addEventListener === "function"
      ) {
        window.addEventListener("message", onMessage);
      }

      // Track popup-closed-early: when the popup closes BEFORE the
      // server reports 'connected'/'expired', wait up to popupClosedGraceMs
      // for any in-flight postMessage (race: user clicks Allow → server
      // sets the PIN to connected → popup closes → polling catches up
      // on the next tick). If the next poll still says pending, surface
      // 'popup_closed'.
      let popupClosedAt = null;

      const cleanup = () => {
        if (
          typeof window !== "undefined" &&
          typeof window.removeEventListener === "function"
        ) {
          try {
            window.removeEventListener("message", onMessage);
          } catch {
            /* ignore */
          }
        }
        // Close popup defensively if still open (e.g. on timeout).
        if (popup && !popup.closed) {
          try {
            popup.close();
          } catch {
            /* ignore */
          }
        }
        // Drop the reactive in-flight state + the localStorage breadcrumb
        // so the UI fallback panel hides and a future page mount doesn't
        // try to resume a flow that's already done.
        this.pendingPlexConnect = null;
        _clearPendingPlexConnect();
      };

      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Sleep that the postMessage handler can interrupt via wakeSleep().
      // Used between poll ticks so a "I just got back from plex.tv"
      // message cuts the next poll's delay from pollIntervalMs to ~0.
      const interruptibleSleep = (ms) =>
        new Promise((resolve) => {
          const timer = setTimeout(() => {
            wakeSleep = null;
            resolve();
          }, ms);
          wakeSleep = () => {
            clearTimeout(timer);
            resolve();
          };
        });

      // 4) Poll loop. Wait pollIntervalMs, then poll, then check status.
      // The first sleep can be interrupted early by a postMessage from
      // the return view so the success case feels snappy. We sleep at
      // the top so the user has time to even see the popup before the
      // first request hits the wire.
      try {
        while (Date.now() < deadline) {
          await interruptibleSleep(pollIntervalMs);
          if (Date.now() >= deadline) break;
          // User-initiated cancel via cancelPendingPlexConnect() clears
          // this state; bail out so the consuming component's `finally`
          // resets `connecting` and a retry click actually fires.
          if (!this.pendingPlexConnect) {
            return { ok: false, reason: "cancelled" };
          }

          let res;
          try {
            res = await sourcesApi.pollPlexConnect(pinId);
          } catch (err) {
            // 403 wipes the store + fires a toast via _handleError.
            // Other errors set this.error; we keep polling unless it's
            // a 403/permission revocation (cleared above) — in which
            // case `canConnect` flips false and we bail.
            this._handleError(err);
            if (!this.canConnect) {
              return { ok: false, reason: "no_permission" };
            }
            // Continue polling on transient errors (network blip / 5xx).
            continue;
          }

          const status = res?.status;
          if (status === "connected") {
            // Refresh the list so the new card appears (and an
            // existing account UPSERT updates in place — the backend
            // dedupes by plex_user_id, so this won't create a
            // duplicate row).
            await this.loadPlex();
            // Brief grace so the popup can render "Connected" and
            // self-close before we yank the reference.
            await sleep(Math.min(popupClosedGraceMs, 200));
            return {
              ok: true,
              accountId: res?.accountId ?? null,
              account: res?.account ?? null,
            };
          }
          if (status === "expired") {
            return { ok: false, reason: "expired" };
          }

          // status === 'pending' (or unknown). If we managed to open a
          // popup, watch for the user closing it early — that's a
          // useful signal. If we DIDN'T open one (popup blocker), the
          // user is expected to authorize via the fallback link, and
          // there's no popup-closed signal to read.
          if (popup) {
            if (popup.closed) {
              if (popupClosedAt == null) popupClosedAt = Date.now();
              if (Date.now() - popupClosedAt >= popupClosedGraceMs) {
                return { ok: false, reason: "popup_closed" };
              }
            } else {
              popupClosedAt = null;
            }
          }
        }
        return { ok: false, reason: "timeout" };
      } catch (err) {
        return { ok: false, reason: "error", error: err };
      } finally {
        cleanup();
      }
    },

    /**
     * Resume polling for a Plex PIN that was started in a prior page
     * load — e.g. the user clicked "Open in this tab" in the popup-
     * blocked fallback, completed auth on plex.tv, then navigated back
     * to /settings. The persisted `{ pinId, authUrl, deadline }` is
     * read from localStorage and we drive the same poll loop without
     * re-asking the server for a fresh PIN (which would require the
     * user to re-authorize). Idempotent — a no-op when there's no
     * pending pin OR the pin is already expired OR a flow is currently
     * in-flight in this tab. Safe to call on every ConnectedSourcesSection
     * mount.
     *
     * Same return shape as connectPlexFlow.
     */
    async resumePendingPlexConnect(opts = {}) {
      // Skip if a flow is already running in this tab (we'd double-poll).
      if (this.pendingPlexConnect)
        return { ok: false, reason: "already_running" };
      if (!this.canConnect) return { ok: false, reason: "no_permission" };

      const persisted = _readPendingPlexConnect();
      if (!persisted) return { ok: false, reason: "no_pending" };

      const { pinId, authUrl, deadline } = persisted;
      const pollIntervalMs = Number.isFinite(opts.pollIntervalMs)
        ? opts.pollIntervalMs
        : 1000;

      // Same shape as connectPlexFlow's reactive state — popupBlocked
      // defaults true here because if you're resuming, you almost
      // certainly came back from an out-of-band auth attempt; the UI
      // panel is the right affordance to keep visible.
      this.pendingPlexConnect = {
        pinId,
        authUrl,
        popupBlocked: true,
        expiresAt: deadline,
      };

      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      try {
        while (Date.now() < deadline) {
          await sleep(pollIntervalMs);
          if (Date.now() >= deadline) break;
          // Cancelled by the UI fallback's Cancel button.
          if (!this.pendingPlexConnect) {
            return { ok: false, reason: "cancelled" };
          }

          let res;
          try {
            res = await sourcesApi.pollPlexConnect(pinId);
          } catch (err) {
            this._handleError(err);
            if (!this.canConnect) {
              return { ok: false, reason: "no_permission" };
            }
            continue;
          }

          const status = res?.status;
          if (status === "connected") {
            await this.loadPlex();
            return {
              ok: true,
              accountId: res?.accountId ?? null,
              account: res?.account ?? null,
            };
          }
          if (status === "expired") {
            return { ok: false, reason: "expired" };
          }
        }
        return { ok: false, reason: "timeout" };
      } catch (err) {
        return { ok: false, reason: "error", error: err };
      } finally {
        this.pendingPlexConnect = null;
        _clearPendingPlexConnect();
      }
    },

    /**
     * Cancel an in-flight Plex PIN flow. Clears the reactive state and
     * the persisted breadcrumb so the next tab visit doesn't try to
     * resume. The polling loop in connectPlexFlow / resumePendingPlexConnect
     * is left running — it will detect the cleared state on its next
     * tick and exit naturally. The user-facing affordance is "Cancel" in
     * the popup-blocked fallback panel.
     */
    cancelPendingPlexConnect() {
      this.pendingPlexConnect = null;
      _clearPendingPlexConnect();
    },

    async disconnectPlex(accountId) {
      if (!this.canConnect) return false;
      this.error = null;
      try {
        await sourcesApi.disconnectPlexAccount(accountId);
        // Optimistically prune the local row, then refresh from the server
        // so reachability / status fields stay in sync with reality.
        this.plexAccounts = this.plexAccounts.filter(
          (a) => Number(a?.accountId ?? a?.id) !== Number(accountId),
        );
        await this.loadPlex();
        return true;
      } catch (err) {
        this._handleError(err);
        return false;
      }
    },

    async refreshPlex(accountId) {
      if (!this.canConnect) return null;
      this.error = null;
      try {
        const res = await sourcesApi.refreshPlexAccount(accountId);
        // The refresh endpoint returns the updated account object — splice it
        // back into `plexAccounts` so the UI re-renders without a full reload.
        if (res && typeof res === "object") {
          const idx = this.plexAccounts.findIndex(
            (a) =>
              Number(a?.accountId ?? a?.id) ===
              Number(res?.accountId ?? accountId),
          );
          if (idx !== -1) {
            const next = this.plexAccounts.slice();
            next[idx] = { ...next[idx], ...res };
            this.plexAccounts = next;
          }
        }
        return res;
      } catch (err) {
        this._handleError(err);
        return null;
      }
    },

    // -- Jellyfin --------------------------------------------------------

    async loadJellyfin() {
      // Feature 28: see loadPlex — read paths gate on `canUse` so sharees
      // see shared Jellyfin servers in /settings.
      if (!this.canUse) {
        this.jellyfinServers = [];
        return [];
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await sourcesApi.listServers();
        this.jellyfinServers = Array.isArray(res)
          ? res
          : Array.isArray(res?.servers)
            ? res.servers
            : [];
        return this.jellyfinServers;
      } catch (err) {
        this._handleError(err);
        return [];
      } finally {
        this.loading = false;
      }
    },

    async addJellyfin(payload) {
      if (!this.canConnect) return null;
      this.error = null;
      try {
        const res = await sourcesApi.connectJellyfin(payload);
        // Re-load so the new row shows up with its server-assigned id and
        // status. The /connect endpoint returns the row but a fresh GET
        // also surfaces any other servers that may have been added in
        // another tab.
        await this.loadJellyfin();
        return res;
      } catch (err) {
        this._handleError(err);
        return null;
      }
    },

    async disconnectJellyfin(serverId) {
      if (!this.canConnect) return false;
      this.error = null;
      try {
        await sourcesApi.disconnectJellyfinServer(serverId);
        this.jellyfinServers = this.jellyfinServers.filter(
          (s) => Number(s?.serverId ?? s?.id) !== Number(serverId),
        );
        await this.loadJellyfin();
        return true;
      } catch (err) {
        this._handleError(err);
        return false;
      }
    },

    async refreshJellyfin(serverId) {
      if (!this.canConnect) return null;
      this.error = null;
      try {
        const res = await sourcesApi.refreshJellyfinServer(serverId);
        if (res && typeof res === "object") {
          const idx = this.jellyfinServers.findIndex(
            (s) =>
              Number(s?.serverId ?? s?.id) ===
              Number(res?.serverId ?? serverId),
          );
          if (idx !== -1) {
            const next = this.jellyfinServers.slice();
            next[idx] = { ...next[idx], ...res };
            this.jellyfinServers = next;
          }
        }
        return res;
      } catch (err) {
        this._handleError(err);
        return null;
      }
    },

    // -- Source match ----------------------------------------------------

    /**
     * Resolve cross-source matches for a TMDB id. Returns an array (possibly
     * empty). Concurrent calls for the same (tmdbId, type) share a single
     * in-flight Promise, and successful results are cached in-memory for
     * ~30s so a /discover grid of repeating items doesn't fan out N
     * identical requests.
     *
     * NEVER throws — returns [] on error so MediaCards can stay simple.
     */
    async getMatch(tmdbId, type) {
      const { matches } = await this.getMatchDetailed(tmdbId, type);
      return matches;
    },

    /**
     * Cross-source match as `{ matches, incomplete }`.
     *
     * `incomplete: true` means a server never answered, so an empty `matches`
     * must not be read as "not in the library". Critically, an incomplete
     * answer is NOT written to the in-memory cache — caching it would pin the
     * wrong answer for the full TTL and reproduce the exact bug the flag
     * exists to prevent.
     */
    async getMatchDetailed(tmdbId, type) {
      if (!this.canUse) return { matches: [], incomplete: false };
      const normalizedTmdbId = normalizeTmdbId(tmdbId);
      if (normalizedTmdbId == null || (type !== "movie" && type !== "tv")) {
        return { matches: [], incomplete: false };
      }

      const now = Date.now();
      // Rate-limited: we have no answer at all, which is not the same as an
      // empty library.
      if (matchRateLimitedUntil > now) return { matches: [], incomplete: true };

      const key = matchKey(normalizedTmdbId, type);

      // Cache hit — return immediately, no network. Bump insertion order so
      // recently-read keys aren't first in line for eviction (approximates LRU).
      const cached = matchCache.get(key);
      if (cached && cached.expiresAt > now) {
        matchCache.delete(key);
        matchCache.set(key, cached);
        return cached.value;
      }

      // In-flight dedupe — share the same Promise across concurrent callers.
      const existing = matchInflight.get(key);
      if (existing) return existing;

      const promise = (async () => {
        try {
          const res = await sourcesApi.getMatchDetailed(normalizedTmdbId, type);
          // Older proxy builds answer the bare array (no `detail` support) —
          // treat that as a complete answer, same as before.
          const matches = Array.isArray(res)
            ? res
            : Array.isArray(res?.matches)
              ? res.matches
              : [];
          const incomplete = !Array.isArray(res) && res?.incomplete === true;
          const value = { matches, incomplete };
          // Only cache an ANSWER. An incomplete result would otherwise pin
          // "not in your library" for the whole TTL even though a server
          // simply hadn't replied yet.
          if (!incomplete) {
            setMatchCacheEntry(key, {
              value,
              expiresAt: Date.now() + MATCH_CACHE_TTL_MS,
            });
          }
          return value;
        } catch (err) {
          if (err instanceof ApiError && err.status === 429) {
            const retryAfterSeconds = Number(err?.body?.retryAfter ?? 0);
            const retryAfterMs =
              Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
                ? retryAfterSeconds * 1000
                : 15_000;
            matchRateLimitedUntil = Date.now() + retryAfterMs;
            return { matches: [], incomplete: true };
          }
          // Don't cache failures — let the caller retry on the next render
          // pass. Surface the error via _handleError so 403s clear the store.
          this._handleError(err, { silent: false });
          // A 403 is a definitive "this invite can't use external sources";
          // anything else left the question unanswered.
          const forbidden = err instanceof ApiError && err.status === 403;
          return { matches: [], incomplete: !forbidden };
        } finally {
          matchInflight.delete(key);
        }
      })();

      matchInflight.set(key, promise);
      return promise;
    },

    // -- Local clean-up (e.g. on logout) ---------------------------------

    reset() {
      this.plexAccounts = [];
      this.jellyfinServers = [];
      this.loading = false;
      this.error = null;
      matchInflight.clear();
      matchCache.clear();
      matchRateLimitedUntil = 0;
      permissionToastFired = false;
    },
  },
});
